import React, { useState, useEffect, useCallback } from 'react';
import { Loader, TableLoader } from '../../../components/Common/Loader';
import { CustomDropdown } from '../../../components/Common/CustomDropdown';
import { Search, X, ArrowLeft, Eye, MapPin, CheckCircle2, XCircle, Building2, Filter } from 'lucide-react';
import { getTerritoriesAPI, getTerritoryByIdAPI, getZonesAPI, getCitiesAPI } from '../../../api/locations';
import CSVButton from '../../../components/Common/CSVButton';
import { exportToCSV } from '../../../utils/csvExport';

export interface TerritoryZone {
  id: number;
  name: string;
}

export interface TerritoryCity {
  id: number;
  name: string;
  sapCode?: string | null;
  status?: number | string;
}

export interface TerritoryItem {
  id: number;
  name: string;
  zoneId?: number;
  status?: number | string;
  zone?: TerritoryZone;
  cities?: TerritoryCity[];
  citiesCount?: number;
  activeCities?: number | TerritoryCity[];
  inactiveCities?: number | TerritoryCity[];
  activeCitiesCount?: number;
  inactiveCitiesCount?: number;
  totalCities?: number;
}

const initialTerritoriesMock: TerritoryItem[] = [
  {
    id: 3,
    name: 'Pune',
    zoneId: 1,
    status: 1,
    zone: { id: 1, name: 'WEST' },
    cities: [
      { id: 5, name: 'Pune City', sapCode: 'PNE', status: 1 },
      { id: 14, name: 'Pimpri-Chinchwad', sapCode: 'PCMC', status: 1 },
      { id: 15, name: 'Haveli', sapCode: 'HVL', status: 0 },
    ],
  },
  {
    id: 4,
    name: 'Aurangabad',
    zoneId: 1,
    status: 1,
    zone: { id: 1, name: 'WEST' },
    cities: [
      { id: 9, name: 'Aurangabad', sapCode: 'AUR', status: 1 },
      { id: 16, name: 'Jalna', sapCode: 'JLN', status: 1 },
    ],
  },
  {
    id: 6,
    name: 'Bangalore',
    zoneId: 2,
    status: 1,
    zone: { id: 2, name: 'SOUTH' },
    cities: [
      { id: 12, name: 'Bangalore Urban', sapCode: 'BLRU', status: 1 },
      { id: 17, name: 'Bangalore Rural', sapCode: 'BLRR', status: 0 },
    ],
  },
  {
    id: 7,
    name: 'Chennai',
    zoneId: 2,
    status: 1,
    zone: { id: 2, name: 'SOUTH' },
    cities: [
      { id: 13, name: 'Chennai Central', sapCode: 'MAAC', status: 1 },
    ],
  },
];

export const TerritoriesList: React.FC = () => {
  const [territories, setTerritories] = useState<TerritoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown selection state
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<number | 'all'>('all');
  const [selectedTerritoryDetail, setSelectedTerritoryDetail] = useState<TerritoryItem | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  // Detail Modal state (GET /api/v1/locations/territories/:id)
  const [viewingTerritory, setViewingTerritory] = useState<TerritoryItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const calculateCitiesCount = (item: TerritoryItem, allFetchedCities?: any[]) => {
    // 1. Resolve cities array safely
    let citiesArr: TerritoryCity[] = [];
    if (Array.isArray(item.cities) && item.cities.length > 0) {
      citiesArr = item.cities;
    } else if (Array.isArray(item.activeCities) || Array.isArray(item.inactiveCities)) {
      const activeArr = Array.isArray(item.activeCities) ? item.activeCities : [];
      const inactiveArr = Array.isArray(item.inactiveCities) ? item.inactiveCities : [];
      citiesArr = [...activeArr, ...inactiveArr];
    } else if (Array.isArray(allFetchedCities)) {
      citiesArr = allFetchedCities.filter(
        (c: any) => c.territoryId === item.id || c.territory?.id === item.id
      );
    }

    // 2. Resolve active cities count (numeric)
    let activeCount = 0;
    if (Array.isArray(item.activeCities)) {
      activeCount = item.activeCities.length;
    } else if (typeof item.activeCities === 'number') {
      activeCount = item.activeCities;
    } else {
      activeCount = citiesArr.filter(
        (c) => c.status === 1 || c.status === 'Active' || c.status === undefined || c.status === null
      ).length;
    }

    // 3. Resolve inactive cities count (numeric)
    let inactiveCount = 0;
    if (Array.isArray(item.inactiveCities)) {
      inactiveCount = item.inactiveCities.length;
    } else if (typeof item.inactiveCities === 'number') {
      inactiveCount = item.inactiveCities;
    } else {
      inactiveCount = citiesArr.filter(
        (c) => c.status === 0 || c.status === 'Inactive'
      ).length;
    }

    // 4. Resolve total cities count (numeric)
    let totalCount = 0;
    if (typeof item.citiesCount === 'number') {
      totalCount = item.citiesCount;
    } else {
      totalCount = activeCount + inactiveCount;
    }

    return {
      activeCitiesCount: activeCount,
      inactiveCitiesCount: inactiveCount,
      totalCities: totalCount,
      resolvedCities: citiesArr,
    };
  };

  const fetchTerritories = useCallback(async () => {
    try {
      setLoading(true);
      const [resTerritories, , resCities] = await Promise.allSettled([
        getTerritoriesAPI(),
        getZonesAPI(),
        getCitiesAPI(),
      ]);

      let rawTerritories: TerritoryItem[] = [];
      if (resTerritories.status === 'fulfilled' && resTerritories.value) {
        const val = resTerritories.value;
        rawTerritories = val?.data || val?.territories || (Array.isArray(val) ? val : []);
      }

      let rawCities: any[] = [];
      if (resCities.status === 'fulfilled' && resCities.value) {
        const val = resCities.value;
        rawCities = val?.data || val?.cities || (Array.isArray(val) ? val : []);
      }

      const listToProcess = (Array.isArray(rawTerritories) && rawTerritories.length > 0)
        ? rawTerritories
        : initialTerritoriesMock;

      const processed = listToProcess.map((t) => {
        const stats = calculateCitiesCount(t, rawCities);
        return {
          ...t,
          cities: stats.resolvedCities,
          activeCitiesCount: stats.activeCitiesCount,
          inactiveCitiesCount: stats.inactiveCitiesCount,
          totalCities: stats.totalCities,
        };
      });

      setTerritories(processed);
    } catch (err) {
      console.warn('Using fallback data for territories:', err);
      const processed = initialTerritoriesMock.map((t) => {
        const stats = calculateCitiesCount(t);
        return {
          ...t,
          cities: stats.resolvedCities,
          activeCitiesCount: stats.activeCitiesCount,
          inactiveCitiesCount: stats.inactiveCitiesCount,
          totalCities: stats.totalCities,
        };
      });
      setTerritories(processed);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTerritories();
  }, [fetchTerritories]);

  // Handle Territory dropdown change - calls GET /api/v1/locations/territories/:id
  const handleSelectTerritory = async (val: string) => {
    if (val === 'all') {
      setSelectedTerritoryId('all');
      setSelectedTerritoryDetail(null);
      return;
    }

    const tId = Number(val);
    setSelectedTerritoryId(tId);
    setSelectedLoading(true);

    try {
      const res = await getTerritoryByIdAPI(tId);
      const detailData = res?.data || res?.territory || res;
      if (detailData && typeof detailData === 'object' && detailData.id) {
        const stats = calculateCitiesCount(detailData);
        setSelectedTerritoryDetail({
          ...detailData,
          cities: stats.resolvedCities,
          activeCitiesCount: stats.activeCitiesCount,
          inactiveCitiesCount: stats.inactiveCitiesCount,
          totalCities: stats.totalCities,
        });
      } else {
        const matched = territories.find((t) => t.id === tId);
        if (matched) {
          const stats = calculateCitiesCount(matched);
          setSelectedTerritoryDetail({
            ...matched,
            cities: stats.resolvedCities,
            activeCitiesCount: stats.activeCitiesCount,
            inactiveCitiesCount: stats.inactiveCitiesCount,
            totalCities: stats.totalCities,
          });
        }
      }
    } catch (err) {
      console.warn('Failed to fetch territory by id, falling back to local item:', err);
      const matched = territories.find((t) => t.id === tId);
      if (matched) {
        const stats = calculateCitiesCount(matched);
        setSelectedTerritoryDetail({
          ...matched,
          cities: stats.resolvedCities,
          activeCitiesCount: stats.activeCitiesCount,
          inactiveCitiesCount: stats.inactiveCitiesCount,
          totalCities: stats.totalCities,
        });
      }
    } finally {
      setSelectedLoading(false);
    }
  };

  useEffect(() => {
    fetchTerritories();
  }, [fetchTerritories]);

  const handleViewDetails = async (t: TerritoryItem) => {
    setViewingTerritory(t);
    setDetailLoading(true);
    try {
      const res = await getTerritoryByIdAPI(t.id);
      const detailData = res?.data || res?.territory || res;
      if (detailData && typeof detailData === 'object' && detailData.id) {
        const stats = calculateCitiesCount(detailData);
        setViewingTerritory({
          ...detailData,
          cities: stats.resolvedCities,
          activeCitiesCount: stats.activeCitiesCount,
          inactiveCitiesCount: stats.inactiveCitiesCount,
          totalCities: stats.totalCities,
        });
      }
    } catch (err) {
      console.warn('Failed to fetch territory by id, using local state:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredTerritories = territories.filter(
    (t) =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.zone && t.zone.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const totalActiveCitiesAcrossAll = territories.reduce((acc, t) => acc + (t.activeCitiesCount || 0), 0);
  const totalInactiveCitiesAcrossAll = territories.reduce((acc, t) => acc + (t.inactiveCitiesCount || 0), 0);

  // Active cities & inactive cities for currently selected territory dropdown
  const currentActiveCities = selectedTerritoryDetail
    ? selectedTerritoryDetail.activeCitiesCount ?? 0
    : totalActiveCitiesAcrossAll;
  const currentInactiveCities = selectedTerritoryDetail
    ? selectedTerritoryDetail.inactiveCitiesCount ?? 0
    : totalInactiveCitiesAcrossAll;

  const handleExportCSV = () => {
    const headers = ['ID', 'Territory Name', 'Zone', 'Active Cities', 'Inactive Cities', 'Total Cities', 'Status'];
    const rows = filteredTerritories.map((t) => [
      t.id,
      t.name,
      t.zone?.name || '',
      t.activeCitiesCount ?? 0,
      t.inactiveCitiesCount ?? 0,
      t.totalCities ?? 0,
      t.status === 1 || t.status === '1' || t.status === 'Active' ? 'Active' : 'Inactive',
    ]);
    exportToCSV('territories_list', headers, rows);
  };

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
              Territories Management
            </h1>
            <p className="text-xs text-slate-500 font-medium">
              View territories and their assigned active & inactive cities
            </p>
          </div>
        </div>
        <CSVButton onClick={handleExportCSV} />
      </div>

      {/* Territory Selection Bar */}
      <div className="bg-white rounded-2xl p-4 bg-gradient-to-r from-amber-50/40 to-white shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#C1272D]/10 text-[#C1272D] flex items-center justify-center font-bold">
            <Filter className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-extrabold text-slate-800">Select Territory</h3>
            <p className="text-[11px] text-slate-500 font-medium">
              Choose a territory to view its calculated active & inactive cities
            </p>
          </div>
        </div>

        <div className="w-full sm:w-72">
          <CustomDropdown
            placeholder="Select Territory"
            options={[
              { id: 'all', name: `All Territories (${territories.length})` },
              ...territories.map((t) => ({
                id: String(t.id),
                name: `${t.name}`,
              })),
            ]}
            value={
              selectedTerritoryId === 'all'
                ? `All Territories (${territories.length})`
                : territories.find((t) => t.id === Number(selectedTerritoryId))?.name || `All Territories (${territories.length})`
            }
            onChange={(selectedName: string) => {
              if (selectedName.startsWith('All Territories')) {
                handleSelectTerritory('all');
              } else {
                const matched = territories.find((t) => t.name === selectedName);
                if (matched) {
                  handleSelectTerritory(String(matched.id));
                } else {
                  handleSelectTerritory('all');
                }
              }
            }}
          />
        </div>
      </div>

      {/* Summary Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center font-bold">
            <Building2 className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              {selectedTerritoryDetail ? 'Territory Selected' : 'Total Territories'}
            </p>
            <h3 className="text-2xl font-black text-slate-800">
              {selectedTerritoryDetail ? selectedTerritoryDetail.name : territories.length}
            </h3>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 border border-emerald-100/80 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider">
              Active Cities {selectedTerritoryDetail ? `(${selectedTerritoryDetail.name})` : ''}
            </p>
            <h3 className="text-2xl font-black text-emerald-700">{currentActiveCities}</h3>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 border border-rose-100/80 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold">
            <XCircle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-rose-600 uppercase tracking-wider">
              Inactive Cities {selectedTerritoryDetail ? `(${selectedTerritoryDetail.name})` : ''}
            </p>
            <h3 className="text-2xl font-black text-rose-700">{currentInactiveCities}</h3>
          </div>
        </div>
      </div>

      {/* Main Content Area: If a territory is selected from dropdown, show its assigned cities table */}
      {selectedTerritoryDetail ? (
        <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col space-y-4 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-2">
            <div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#C1272D]" />
                <h3 className="text-lg font-black text-slate-800">
                  {selectedTerritoryDetail.name} Territory Cities
                </h3>
                <span className="px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold text-xs">
                  {selectedTerritoryDetail.zone?.name || 'WEST Zone'}
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium mt-1">
                Displaying cities under {selectedTerritoryDetail.name}
              </p>
            </div>

            <button
              onClick={() => {
                setSelectedTerritoryId('all');
                setSelectedTerritoryDetail(null);
              }}
              className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
            >
              View All Territories
            </button>
          </div>

          {selectedLoading ? (
            <Loader text="Loading..." className="py-12" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                    <th className="py-3.5 px-6">ID</th>
                    <th className="py-3.5 px-6">City Name</th>
                    <th className="py-3.5 px-6">Territory</th>
                    <th className="py-3.5 px-6">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                  {selectedTerritoryDetail.cities && selectedTerritoryDetail.cities.length > 0 ? (
                    selectedTerritoryDetail.cities.map((c) => {
                      const isCityActive = c.status === 1 || c.status === 'Active' || c.status === undefined || c.status === null;
                      return (
                        <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                          <td className="py-4 px-6 font-medium text-slate-400">{c.id}</td>
                          <td className="py-4 px-6 font-bold text-slate-800">{c.name || (c as any).companyName}</td>
                          <td className="py-4 px-6 font-medium text-slate-700">{selectedTerritoryDetail.name}</td>
                          <td className="py-4 px-6">
                            <span
                              className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${
                                isCityActive ? 'bg-emerald-100/80 text-emerald-700' : 'bg-rose-100/80 text-rose-700'
                              }`}
                            >
                              {isCityActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-12 text-center text-slate-400 font-medium">
                        No cities assigned to {selectedTerritoryDetail.name} territory yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* All Territories Table */
        <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
          {/* Controls Bar */}
          <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 className="text-base font-bold text-slate-800">All Territories</h3>

            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search territories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 focus:outline-none focus:border-[#C1272D]"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  <th className="py-3.5 px-6">ID</th>
                  <th className="py-3.5 px-6">Territory Name</th>
                  <th className="py-3.5 px-6">Zone</th>
                  <th className="py-3.5 px-6 text-center">Total Cities</th>
                  <th className="py-3.5 px-6">Status</th>
                  <th className="py-3.5 px-6 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                {filteredTerritories.map((t) => {
                  const isActive = t.status === 1 || t.status === 'Active' || t.status === undefined;
                  return (
                    <tr key={t.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-4 px-6 font-medium text-slate-400">{t.id}</td>
                      <td className="py-4 px-6 font-bold text-slate-800 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#C1272D]" />
                        <span>{t.name}</span>
                      </td>
                      <td className="py-4 px-6 font-semibold text-slate-700">
                        <span className="px-2.5 py-1 bg-amber-100/70 text-amber-800 rounded-md text-[11px] font-bold">
                          {t.zone?.name || 'WEST'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center font-bold text-slate-700">
                        {t.totalCities ?? ((t.activeCitiesCount || 0) + (t.inactiveCitiesCount || 0))}
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${
                            isActive
                              ? 'bg-emerald-100/80 text-emerald-700'
                              : 'bg-rose-100/80 text-rose-700'
                          }`}
                        >
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => handleViewDetails(t)}
                            title="View"
                            className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {loading ? (
                  <TableLoader colSpan={6} text="Loading territories..." />
                ) : filteredTerritories.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-400 font-medium">
                      No territories found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Territory Detail Modal (GET /api/v1/locations/territories/:id) */}
      {viewingTerritory && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#C1272D]" />
                <h3 className="text-lg font-extrabold text-slate-800">
                  {viewingTerritory.name} Territory Details
                </h3>
              </div>
              <button
                onClick={() => setViewingTerritory(null)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer hover:bg-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {detailLoading ? (
              <Loader text="Loading details..." className="py-8" />
            ) : (
              <div className="space-y-5 text-xs">
                {/* Information Header Grid */}
                <div className="grid grid-cols-3 gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <div>
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">ID</span>
                    <span className="font-bold text-slate-800">{viewingTerritory.id}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Zone</span>
                    <span className="font-semibold text-amber-700">{viewingTerritory.zone?.name || ''}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Status</span>
                    <span className="font-extrabold text-emerald-600">
                      {viewingTerritory.status === 0 ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                </div>

                {/* Cities Calculation Metrics */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50/70 border border-emerald-200/70 p-3 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase">Active Cities</p>
                    <p className="text-xl font-black text-emerald-700">{viewingTerritory.activeCitiesCount ?? 0}</p>
                  </div>
                  <div className="bg-rose-50/70 border border-rose-200/70 p-3 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-rose-600 uppercase">Inactive Cities</p>
                    <p className="text-xl font-black text-rose-700">{viewingTerritory.inactiveCitiesCount ?? 0}</p>
                  </div>
                  <div className="bg-amber-50/70 border border-amber-200/70 p-3 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-amber-700 uppercase">Total Cities</p>
                    <p className="text-xl font-black text-amber-800">{viewingTerritory.totalCities ?? 0}</p>
                  </div>
                </div>

                {/* List of Cities */}
                <div className="space-y-2">
                  <h4 className="font-bold text-slate-700 text-sm">Assigned Cities ({viewingTerritory.cities?.length || 0})</h4>
                  <div className="max-h-56 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100">
                    {viewingTerritory.cities && viewingTerritory.cities.length > 0 ? (
                      viewingTerritory.cities.map((c) => {
                        const isCityActive = c.status === 1 || c.status === 'Active' || c.status === undefined || c.status === null;
                        return (
                          <div key={c.id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                            <div>
                              <p className="font-bold text-slate-800">{c.name}</p>
                            </div>
                            <span
                              className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                                isCityActive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                              }`}
                            >
                              {isCityActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 text-center text-slate-400">No cities assigned to this territory yet.</div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => setViewingTerritory(null)}
                    className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TerritoriesList;
